import { capitalize } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { CheckboxElement, TextFieldElement } from 'react-hook-form-mui';
import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { DaysEnum, HoursOfOperation } from '@barboxx/types';
import { enumToKeyList } from '@barboxx/utils';

interface Props {
    editable: boolean;
}

export function HoursOfOperationSection({ editable }: Props) {
    const formContext = useFormContext();

    const hasError = !!formContext.formState.errors?.['hoursOfOperations'];

    return (
        <Accordion
            square={true}
            disableGutters
            sx={{
                boxShadow: 'none',
            }}
            // TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                title="Hours of Operation"
                sx={theme => ({
                    paddingX: 3,
                })}
            >
                <Stack direction="column" gap={1}>
                    <Typography variant="h6">Hours of Operation</Typography>
                    {hasError && (
                        <Typography variant="caption" color="error">
                            Check errors below
                        </Typography>
                    )}
                </Stack>
            </AccordionSummary>
            <AccordionDetails
                sx={theme => ({
                    borderTop: `solid 1px ${theme.palette.divider}`,
                    paddingY: 3,
                    paddingX: 3,
                })}
            >
                <Stack gap={2}>
                    {enumToKeyList(DaysEnum).map(day => (
                        <Controller
                            key={day}
                            name={`hoursOfOperations.${day}`}
                            control={formContext.control}
                            render={({ field }) => {
                                // Generated by Copilot
                                const isClosed = field.value === null;

                                return (
                                    <Stack gap={2}>
                                        <Stack
                                            direction="row"
                                            gap={1}
                                            alignItems="center"
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ width: 100 }}
                                            >
                                                {`${capitalize(day)}:`}
                                            </Typography>

                                            {isClosed ? (
                                                <>
                                                    <TextField
                                                        label="Open"
                                                        type="time"
                                                        sx={{ flex: 1 }}
                                                        value=""
                                                        disabled
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                    />
                                                    <TextField
                                                        label="Close"
                                                        type="time"
                                                        sx={{ flex: 1 }}
                                                        value=""
                                                        disabled
                                                        slotProps={{
                                                            input: {
                                                                readOnly: true,
                                                            },
                                                        }}
                                                    />
                                                </>
                                            ) : (
                                                <>
                                                    <TextFieldElement
                                                        label="Open"
                                                        type="time"
                                                        name={`hoursOfOperations.${day}.open`}
                                                        sx={{ flex: 1 }}
                                                        slotProps={{
                                                            input: {
                                                                readOnly:
                                                                    !editable,
                                                                inputMode:
                                                                    'numeric',
                                                            },
                                                            htmlInput: {
                                                                step: 1,
                                                            },
                                                        }}
                                                    />
                                                    <TextFieldElement
                                                        label="Close"
                                                        type="time"
                                                        name={`hoursOfOperations.${day}.close`}
                                                        sx={{ flex: 1 }}
                                                        slotProps={{
                                                            input: {
                                                                readOnly:
                                                                    !editable,
                                                                inputMode:
                                                                    'numeric',
                                                            },
                                                            htmlInput: {
                                                                step: 1,
                                                            },
                                                        }}
                                                    />
                                                </>
                                            )}

                                            <CheckboxElement
                                                name={`hoursOfOperations.${day}`}
                                                label="Open"
                                                disabled={!editable}
                                                transform={{
                                                    input(value) {
                                                        return value !== null;
                                                    },
                                                    output(event, value) {
                                                        if (!value) {
                                                            return null;
                                                        }

                                                        const result: HoursOfOperation =
                                                            {
                                                                open: '14:00:00',
                                                                close: '02:00:00',
                                                            };

                                                        return result;
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Stack>
                                );
                            }}
                        />
                    ))}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
}
